import { ReactElement, useState } from 'react';
import styled from 'styled-components';

import { DocplannerHealthcareProviderConfiguration } from '@dreryk/egabinet-docplanner-frontend-api';

import { useFormController } from '@dreryk/react-common';
import {
   ActionBar,
   ButtonVariant,
   Heading,
   Message,
   MessageType,
   PasswordInput,
   StyledFlex,
   TextInput,
} from '@dreryk/react-components';

import { docplannerTranslationKeys, integrationTranslationKeys } from '../translations';

import { useCurrentDocplannerConfigurationCreateCommand } from './integration.hooks';
import { DocplannerIntegrationFormBean } from './integration.types';
import { SynchronizationDaysCountField } from './SynchronizationDaysCountField';

const StyledDocplannerIntegrationForm = styled(StyledFlex)`
   height: 100%;
   width: 100%;
   max-width: 298px;
`;
StyledDocplannerIntegrationForm.displayName = 'StyledDocplannerIntegrationForm';

const DOCPLANNER_API_KEY_MAX_LENGTH = 100;
const DOCPLANNER_API_SECRET_MAX_LENGTH = 100;

type DocplannerIntegrationFormProps = {
   readonly onConfigurationCreateSuccess: (
      newDocplannerConfiguration: DocplannerHealthcareProviderConfiguration
   ) => void;
};

export const DocplannerIntegrationForm = (props: DocplannerIntegrationFormProps): ReactElement => {
   const [authenticationError, setAuthenticationError] = useState<boolean | undefined>(undefined);
   const { register, handleSubmit } = useFormController<DocplannerIntegrationFormBean>({
      apiKey: undefined,
      apiSecret: undefined,
      synchronizationDaysCount: undefined,
   });
   const handleAuthenticationError = (): void => setAuthenticationError(true);
   const { executeAsync: submitDocplannerConfiguration } = useCurrentDocplannerConfigurationCreateCommand(
      handleAuthenticationError,
      props.onConfigurationCreateSuccess
   );
   const handleDocplannerIntegrationFormSubmit = async (): Promise<void> => {
      setAuthenticationError(false);
      await handleSubmit(submitDocplannerConfiguration)();
   };

   return (
      <ActionBar testId={'docplannerIntegrationConfiguration'} id={'docplannerIntegrationConfiguration'}>
         <Heading
            content={integrationTranslationKeys.integrationConfiguration}
            type={'h2'}
            testId={'docplannerIntegration'}
         />
         <StyledDocplannerIntegrationForm as={'form'} direction={'column'} gap={'24px'} noValidate>
            {authenticationError ? (
               <Message
                  testId={'authenticationError'}
                  type={MessageType.NEGATIVE}
                  label={docplannerTranslationKeys.integration.wrongApiKeyOrPassword}
                  displayIndicator
               />
            ) : null}
            <TextInput
               testId={'apiKey'}
               label={integrationTranslationKeys.apiKey}
               controller={register('apiKey', { required: true, maxLength: DOCPLANNER_API_KEY_MAX_LENGTH })}
            />
            <PasswordInput
               testId={'apiSecret'}
               label={integrationTranslationKeys.apiSecret}
               controller={register('apiSecret', { required: true, maxLength: DOCPLANNER_API_SECRET_MAX_LENGTH })}
            />
            <SynchronizationDaysCountField register={register} />
            <ActionBar.PrimaryButton
               testId={'save'}
               label={integrationTranslationKeys.save}
               onClick={handleDocplannerIntegrationFormSubmit}
               variant={ButtonVariant.PRIMARY}
               blockingOperation
            />
         </StyledDocplannerIntegrationForm>
      </ActionBar>
   );
};
